import StockChip from '@components/application/stockChip';
import VehicleResult from '@components/vehicle_search/vehicleResult';
import useQueryParam from '@javascript/lib/hooks/useQueryParam';
import type {
  EventType,
  InstantSearchResult,
} from '@sharedTypes/InstantSearch';
import type { Vehicle } from '@sharedTypes/Vehicle';
import type { WithExperiments } from '@javascript/types/Experiment';
import useCurrentSortOrder from './hooks/useCurrentSortOrder';

interface InstantSearchVehicleProps {
  hit: InstantSearchResult;
  sendEvent: (
    event: EventType,
    hit: InstantSearchResult,
    label: string,
  ) => void;
  analytics?: () => void;
  branchDistances?: Record<string, { distance_to: number; name: string }>;
  clickAndCollectDetails?: Vehicle['clickAndCollectDetails'];
  hideImageSlider?: boolean;
  hideStockChip?: boolean;
  isShowcaseContent?: boolean;
}

const InstantSearchVehicle = ({
  hideImageSlider,
  hideStockChip,
  hit,
  analytics,
  branchDistances,
  clickAndCollectDetails,
  isShowcaseContent,
  sendEvent,
  experiments,
}: InstantSearchVehicleProps & WithExperiments) => {
  const { branchInfo, stockCount, imageCount, titleInfo, make } = hit;
  const [location] = useQueryParam('location');
  const index = useCurrentSortOrder();

  const excludedFranchiseCodes = ['busc', 'busi', 'mota'];
  const MILES_TO_METERS = 1609;

  const isExcludedFranchise = (branchInfo: InstantSearchResult['branchInfo']) =>
    excludedFranchiseCodes.includes(branchInfo.franchiseCodes?.[0] ?? '');

  const shouldIncludeDistance = (
    hit: InstantSearchResult,
    branchInfo: InstantSearchResult['branchInfo'],
  ) =>
    hit?._rankingInfo?.geoDistance &&
    branchInfo.dmsid &&
    !isExcludedFranchise(branchInfo);

  const getBranchNameAndUrl = () => {
    if (!location) return {};
    const encodedLocation = encodeURIComponent(location);

    if (!branchInfo.dmsid && !isExcludedFranchise(branchInfo)) {
      return {
        name: `Find ${make} branches near ${location}`,
        url: `/find-a-dealer/search/near-${encodedLocation}?franchise_id=${branchInfo?.franchiseCodes?.[0]}`,
      };
    }
    if (isExcludedFranchise(branchInfo)) {
      return {
        url: `/find-a-dealer/search/near-${encodedLocation}?franchise_id[]=${branchInfo?.franchiseCodes?.[0]}&franchise_id[]=${branchInfo?.franchiseCodes?.[1]}`,
      };
    }
    return {};
  };

  const getDistanceToCustomer = () => {
    if (shouldIncludeDistance(hit, branchInfo)) {
      const geoDistance = hit?._rankingInfo?.geoDistance ?? 0;

      const distanceToCustomer = Math.round(
        index?.includes('geolocation')
          ? geoDistance / MILES_TO_METERS
          : geoDistance,
      );

      return {
        distance_to_customer: distanceToCustomer,
      };
    }
    return {};
  };

  const distanceToCustomer = getDistanceToCustomer();

  const vehicleDistanceToCustomer = distanceToCustomer.distance_to_customer;

  const branchDistanceToSelectedClickAndCollectBranch = branchInfo.dmsid
    ? branchDistances?.[branchInfo.dmsid]?.distance_to
    : undefined;

  const selectedClickAndCollectDistanceToCustomer =
    clickAndCollectDetails?.distance_to_customer;

  const isMovable =
    (vehicleDistanceToCustomer ?? 0) >=
      (selectedClickAndCollectDistanceToCustomer ?? Number.POSITIVE_INFINITY) &&
    (branchDistanceToSelectedClickAndCollectBranch ?? 0) > 15;

  const prefix = isMovable
    ? { prefix: 'Move from' }
    : {
        prefix: 'Collect in 2 days from',
        icon: 'collect',
      };

  const modifiedBranchInfo = {
    ...branchInfo,
    ...getBranchNameAndUrl(),
    ...(clickAndCollectDetails && prefix),
    ...distanceToCustomer,
  };

  const sendEventWithHit = (event: EventType, label: string) =>
    sendEvent(event, hit, label);

  return (
    <VehicleResult
      {...hit}
      algoliaQueryID={hit?.__queryID}
      analytics={analytics}
      branch={modifiedBranchInfo}
      clickAndCollectDetails={clickAndCollectDetails}
      experiments={experiments}
      hideImageSlider={hideImageSlider}
      instantSearchAnalytics={sendEventWithHit}
      isMovable={isMovable}
      isShowcaseContent={isShowcaseContent}
      title={titleInfo}
    >
      {stockCount !== undefined && !hideStockChip && (
        <StockChip count={stockCount} imageCount={imageCount} />
      )}
    </VehicleResult>
  );
};

export default InstantSearchVehicle;
